import type { ResponsiveProps } from 'react-grid-layout'
import type { ChecklistTeam } from '~/types/checklist'
import type { IDateRange } from '~/types/date'
import type { DeviceType } from '~/types/device-type'

import { APP_USER_STATUS } from '~/types/app-user'
import { ASSET_STATUS } from '~/types/assets-management'
import { CHECKLIST_STATUS, CHECKLIST_TEMPLATE } from '~/types/checklist'
import { IAP_FREE_TRIAL, IAP_PERIODICITY, IAP_STATUS, IAP_SUB_TYPE } from '~/types/iap'
import { NAVIGATION_GRAPH_STATUS } from '~/types/navigation-graph'
import { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import { REPORT_COMMENT_STATUS, REPORT_REASON, REPORT_STATUS, REPORT_TYPE } from '~/types/report'
import { ML_LABEL, REVIEW_STATUS } from '~/types/review'
import { TEMPLATE_STATUS, TEMPLATE_TYPE } from '~/types/template'
import { TODOLIST_TASK_STATUS } from '~/types/todolist'
import { VERSION_STATUS } from '~/types/version'
import dayjs from '~/utils/dayjs'

export const MILLISECONDS_IN_NANO = 1000000

export const MAX_DAYS_RANGE = 365

export const BR_MAX_DAYS_RANGE = MAX_DAYS_RANGE * 2

export const MAX_RATING_VALUE = 5

export const MAX_TAGS_ITEM = 2

export const TODAY = new Date()

export const EVENT_API_VERSION = 1

export const DEFAULT_DAI_SINGLE_VALUE = ['0']
export const DEFAULT_DAI_MULTIPLE_VALUES = [
  '0',
  '1',
  '3',
  '7',
  '10',
  '14',
  '20',
  '30',
  '40',
  '60',
  '90',
  '120',
  '150',
  '180',
  '210',
  '240',
  '270',
  '300',
  '330',
  '360',
]

export const COMMON_GRID_PROPS: ResponsiveProps = {
  breakpoints: { lg: 1380, md: 600 },
  cols: { lg: 12, md: 12 },
  containerPadding: [0, 0],
  rowHeight: 93,
}

export const DEFAULT_DATE_RANGE: IDateRange = {
  end: dayjs(new Date()).subtract(1, 'day').format('YYYY-MM-DD'),
  start: dayjs(new Date()).subtract(1, 'month').format('YYYY-MM-DD'),
}

export const COOKIES = {
  CHANGELOG: 'changeLog',
  FEATURES: 'x-mwm-analytics-features',
  NEW_VERSION_START_TIME: 'new-version-start-time',
  STORED_APPLICATION: 'selected-application',
  TOKENS: 'x-mwm-access-tokens',
}

export const LOCAL_STORAGE = {
  ANALYTICS_LAST_REFRESH: 'analytics-last-refresh',
  COMPARE_COHORTS_FILTERS: 'compare-cohorts-filters',
  FILTERS_PRESETS: 'filters_presets',
  NAVBAR_COLLAPSE_STATE: 'navbar_collapse_state',
  ONBOARDING: {
    FILES: 'onboarding-files',
    FILES_NAVIGATION: 'onboarding-files-navigation',
  },
  PAGE_CONTAINER_FILTERS: 'page-container-filters',
  STABILITY_BANNER: 'stability-banner',
  UA_DASHBOARD_COLUMNS: 'ua_dashboard_columns',
}

export const SESSION_STORAGE = {
  CATALOG_CATEGORIES_LAYOUT: 'catalog-categories-layout',
  DEPRECATION_WARNED_SERVICES: 'deprecation-warned-services',
  SHOW_EVENTS_ON_CHARTS: 'show-events-on-charts',
}

export const DEFAULT_APPLICATION = 'edjing_mix:ios'

export const SERIES_NAME: { [key: string]: string } = {
  active_subscriptions: 'Active subscriptions',
  ads_attr_revenue: 'Ads Attributed Revenue',
  ads_revenue: 'Ads Revenue',
  amount_product_received_eur: 'Revenue generated',
  cpi: 'CPI',
  dau: 'Daily Active Users',
  dau_by_country: 'Daily Active Users',
  direct_purchases_by_install: 'Direct purchases by Install',
  free_trial: 'Free trials',
  free_trial_by_install: 'Free trials by Install',
  free_trials: 'Free trials',
  full_screen: 'Full screen',
  inapps_attr_revenue: 'In-App Attributed Revenue',
  inapps_revenue: 'In-App Revenue',
  installs: 'Installs',
  margin_percentage: 'Margin %',
  mau: 'Monthly Active Users',
  mau_by_country: 'Monthly Active Users',
  nb_picture_treated_delete_by_has_picture_treated: 'Delete',
  nb_picture_treated_keep_by_has_picture_treated: 'Keep',
  nbu_crash_fatal_by_nbu: 'Fatal',
  nbu_crash_non_fatal_by_nbu: 'Non fatal',
  new_paying_users_by_free_trial: 'New paying users by free trial',
  new_paying_users_by_install: 'New paying users by install',
  new_subscriptions: 'New subscriptions',
  product_purchased: 'Product purchased',
  refund_rate: 'Refund rate',
  refunds: 'Refunds',
  revenue: 'Revenue',
  revenue_ads_attr_by_install: 'Ads Attributed Revenue by Install',
  revenue_by_dau: 'Revenue by DAU',
  revenue_inapp_attr_by_install: 'In-App Attributed Revenue by Install',
  rewarded_video: 'Rewarded Video',
  sessions_count_by_dau: 'Sessions Count by DAU (Average)',
  sessions_count_by_install: 'Sessions Count by Install (Average)',
  sessions_cumulated_length_by_dau: 'Total Sessions Length by DAU (Average)',
  sessions_cumulated_length_by_install: 'Total Sessions Length by Install (Average)',
  spend: 'Spend',
  subscription_active: 'Total',
  subscription_billing_retry_expired: 'Billing retry expired',
  subscription_billing_retry_started: 'Billing retry started',
  subscription_expired: 'Expired',
  subscription_grace_period_expired: 'Grace period expired',
  subscription_grace_period_started: 'Grace period started',
  subscription_in_trial: 'In Trial',
  subscription_in_trial_in_auto_renew: 'In Trial - Renewing',
  subscription_in_trial_not_in_auto_renew: 'In Trial - Not renewing',
  subscription_paying: 'Paying',
  subscription_paying_in_auto_renew: 'Paying - Renewing',
  subscription_paying_not_in_auto_renew: 'Paying - Not renewing',
  subscription_payment: 'Payment',
  subscription_refunded: 'Refunded',
  subscription_renewed: 'Renewed',
  subscription_started: 'Started',
  subscription_trial_converted: 'Trial converted',
  subscription_trial_started: 'Trial started',
  ua_spend: 'UA Spend',
  unsubscriptions: 'Unsubscribes',
  wau: 'Weekly Active Users',
  wau_by_country: 'Weekly Active Users',
}

export const APP_USER_STATUS_MAPPING: Record<APP_USER_STATUS, string> = {
  [APP_USER_STATUS.ACTIVE]: 'Active',
  [APP_USER_STATUS.ARCHIVED]: 'Archived',
  [APP_USER_STATUS.PENDING_DELETION]: 'Pending deletion',
}

export const CHECKLIST_STATUS_MAPPING: Record<CHECKLIST_STATUS, string> = {
  [CHECKLIST_STATUS.DONE]: 'Completed',
  [CHECKLIST_STATUS.IN_PROGRESS]: 'In progress',
  [CHECKLIST_STATUS.SKIPPED]: 'Skipped',
}

export const TODOLIST_TASK_STATUS_MAPPING: Record<TODOLIST_TASK_STATUS, string> = {
  [TODOLIST_TASK_STATUS.DONE]: 'Completed',
  [TODOLIST_TASK_STATUS.IN_PROGRESS]: 'In progress',
  [TODOLIST_TASK_STATUS.SKIPPED]: 'Skipped',
  [TODOLIST_TASK_STATUS.PENDING]: 'Pending',
}

export const REVIEW_STATUS_MAPPING: Record<REVIEW_STATUS, string> = {
  [REVIEW_STATUS.ANSWERED]: 'Answered',
  [REVIEW_STATUS.EDITED]: 'Edited',
  [REVIEW_STATUS.REMOVED]: 'Removed',
  [REVIEW_STATUS.PROPOSE_REPORT]: 'Propose to report',
  [REVIEW_STATUS.REPORTED]: 'Reported',
  [REVIEW_STATUS.UNTREATED_REPORT]: 'Untreated report',
  [REVIEW_STATUS.PENDING_REPORT]: 'Pending report',
  [REVIEW_STATUS.DECIDED_NOT_TO_REPORT]: 'Decided not to report',
}

export const REPORT_TYPE_MAPPING: Record<REPORT_TYPE, string> = {
  [REPORT_TYPE.COMMENT]: 'Comment',
  [REPORT_TYPE.POST]: 'Post',
  [REPORT_TYPE.PROFILE]: 'Profile',
}

export const REPORT_REASON_MAPPING: Record<REPORT_REASON, string> = {
  [REPORT_REASON.SPAM]: 'Spam',
  [REPORT_REASON.SEXUAL_ACTIVITY]: 'Sexual activity',
  [REPORT_REASON.HATE_SPEECH]: 'Hate speech',
  [REPORT_REASON.VIOLENCE]: 'Violence',
  [REPORT_REASON.SALE_PROHIBITED]: 'Sale prohibited',
  [REPORT_REASON.HARASSMENT]: 'Harassment',
  [REPORT_REASON.INTELLECTUAL_PROPERTY]: 'Intellectual property',
  [REPORT_REASON.SELF_HARM]: 'Self harm',
  [REPORT_REASON.SCAM]: 'Scam',
  [REPORT_REASON.FAKE_NEWS]: 'Fake news',
  [REPORT_REASON.DO_NOT_LIKE]: 'Do not like',
}

export const REPORT_STATUS_MAPPING: Record<REPORT_STATUS, string> = {
  [REPORT_STATUS.ACCEPTED]: 'Accepted',
  [REPORT_STATUS.PENDING]: 'Pending',
  [REPORT_STATUS.REJECTED]: 'Rejected',
}

export const REPORT_COMMENT_STATUS_MAPPING: Record<REPORT_COMMENT_STATUS, string> = {
  [REPORT_COMMENT_STATUS.PENDING_ADMIN_REVIEW]: 'Pending admin review',
  [REPORT_COMMENT_STATUS.PUBLISHED]: 'Published',
  [REPORT_COMMENT_STATUS.REMOVED_BY_ADMIN]: 'Removed by admin',
  [REPORT_COMMENT_STATUS.REMOVED_BY_USER]: 'Removed by user',
}

export const IAP_STATUS_LABEL_MAPPING: Record<IAP_STATUS, string> = {
  [IAP_STATUS.ACTIVE]: 'Active',
  [IAP_STATUS.ACTIVE_2]: 'Active',
  [IAP_STATUS.APPROVED]: 'Approved',
  [IAP_STATUS.DRAFT]: 'Draft',
  [IAP_STATUS.INACTIVE]: 'Inactive',
  [IAP_STATUS.INACTIVE_2]: 'Inactive',
  [IAP_STATUS.DEVELOPER_REMOVED_FROM_SALE]: 'Developer removed from sale',
  [IAP_STATUS.MISSING_METADATA]: 'Missing metadata',
  [IAP_STATUS.READY_TO_SUBMIT]: 'Ready to submit',
  [IAP_STATUS.REJECTED]: 'Rejected',
  [IAP_STATUS.REJECTED_2]: 'Rejected',
  [IAP_STATUS.WAITING_FOR_SCREENSHOT]: 'Waiting for screenshot',
}

export const IAP_SUB_TYPE_LABEL_MAPPING: Record<IAP_SUB_TYPE, string> = {
  [IAP_SUB_TYPE.CONSUMABLE]: 'Consumable',
  [IAP_SUB_TYPE.NON_CONSUMABLE]: 'Non consumable',
  [IAP_SUB_TYPE.ONE_MONTH]: 'Monthly',
  [IAP_SUB_TYPE.ONE_YEAR]: 'Yearly',
  [IAP_SUB_TYPE.ONE_WEEK]: 'Weekly',
  [IAP_SUB_TYPE.SIX_MONTHS]: '6M',
  [IAP_SUB_TYPE.THREE_MONTHS]: '3M',
}

export const IAP_PERIODICITY_LABEL_MAPPING: Record<IAP_PERIODICITY, string> = {
  [IAP_PERIODICITY.WEEKLY]: 'Weekly',
  [IAP_PERIODICITY.TWO_WEEKS]: '2 Weeks',
  [IAP_PERIODICITY.MONTHLY]: 'Monthly',
  [IAP_PERIODICITY.TWO_MONTHS]: '2 Months',
  [IAP_PERIODICITY.THREE_MONTHS]: '3 Months',
  [IAP_PERIODICITY.SIX_MONTHS]: '6 Months',
  [IAP_PERIODICITY.YEARLY]: 'Yearly',
}

export const IAP_PERIODICITY_FILTERS_MAPPING: Record<IAP_PERIODICITY, string> = {
  [IAP_PERIODICITY.WEEKLY]: 'period_weekly',
  [IAP_PERIODICITY.TWO_WEEKS]: 'period_two_weeks',
  [IAP_PERIODICITY.MONTHLY]: 'period_monthly',
  [IAP_PERIODICITY.TWO_MONTHS]: 'period_two_months',
  [IAP_PERIODICITY.THREE_MONTHS]: 'period_three_months',
  [IAP_PERIODICITY.SIX_MONTHS]: 'period_six_months',
  [IAP_PERIODICITY.YEARLY]: 'period_yearly',
}

export const IAP_FREE_TRIAL_LABEL_MAPPING: Record<IAP_FREE_TRIAL, string> = {
  [IAP_FREE_TRIAL.ONE_DAY]: '1 Day',
  [IAP_FREE_TRIAL.THREE_DAYS]: '3 Days',
  [IAP_FREE_TRIAL.ONE_WEEK]: '1 Week',
  [IAP_FREE_TRIAL.TWO_WEEKS]: '2 Weeks',
  [IAP_FREE_TRIAL.ONE_MONTH]: '1 Month',
  [IAP_FREE_TRIAL.TWO_MONTHS]: '2 Months',
  [IAP_FREE_TRIAL.THREE_MONTHS]: '3 Months',
  [IAP_FREE_TRIAL.SIX_MONTHS]: '6 Months',
  [IAP_FREE_TRIAL.ONE_YEAR]: '1 Year',
}

export const IAP_FREE_TRIAL_FILTERS_MAPPING: Record<IAP_FREE_TRIAL, string> = {
  [IAP_FREE_TRIAL.ONE_DAY]: 'free_trial_one_day',
  [IAP_FREE_TRIAL.THREE_DAYS]: 'free_trial_three_days',
  [IAP_FREE_TRIAL.ONE_WEEK]: 'free_trial_one_week',
  [IAP_FREE_TRIAL.TWO_WEEKS]: 'free_trial_two_weeks',
  [IAP_FREE_TRIAL.ONE_MONTH]: 'free_trial_one_month',
  [IAP_FREE_TRIAL.TWO_MONTHS]: 'free_trial_two_monthes',
  [IAP_FREE_TRIAL.THREE_MONTHS]: 'free_trial_three_months',
  [IAP_FREE_TRIAL.SIX_MONTHS]: 'free_trial_six_monthes',
  [IAP_FREE_TRIAL.ONE_YEAR]: 'free_trial_one_year',
}

export const ASSET_STATUS_LABEL_MAPPING: Record<ASSET_STATUS, string> = {
  [ASSET_STATUS.DRAFT_ABORTED]: 'Aborted',
  [ASSET_STATUS.DEPRECATED]: 'Deprecated',
  [ASSET_STATUS.DRAFT]: 'Draft',
  [ASSET_STATUS.RELEASED]: 'Released',
  [ASSET_STATUS.STOPPED]: 'Diffusion stopped',
}

export const NAVIGATION_GRAPH_STATUS_LABEL_MAPPING: Record<NAVIGATION_GRAPH_STATUS, string> = {
  [NAVIGATION_GRAPH_STATUS.DRAFT]: 'Draft',
  [NAVIGATION_GRAPH_STATUS.READY]: 'Ready',
  [NAVIGATION_GRAPH_STATUS.STOPPED]: 'Diffusion stopped',
}

export const PAGE_CONTAINER_STATUS_LABEL_MAPPING: Record<PAGE_CONTAINER_STATUS, string> = {
  [PAGE_CONTAINER_STATUS.ACTIVE]: 'Active',
  [PAGE_CONTAINER_STATUS.ARCHIVED]: 'Archived',
  [PAGE_CONTAINER_STATUS.COMPILED]: 'Compiled',
  [PAGE_CONTAINER_STATUS.DRAFT]: 'Draft',
  [PAGE_CONTAINER_STATUS.PREPARED]: 'Prepared',
  [PAGE_CONTAINER_STATUS.STOPPED]: 'Diffusion stopped',
}

export const TEMPLATE_STATUS_LABEL_MAPPING: Record<TEMPLATE_STATUS, string> = {
  [TEMPLATE_STATUS.READY]: 'Ready',
  [TEMPLATE_STATUS.DRAFT]: 'Draft',
  [TEMPLATE_STATUS.ARCHIVED]: 'Archived',
}

export const TEMPLATE_TYPE_LABEL_MAPPING: Record<TEMPLATE_TYPE, string> = {
  [TEMPLATE_TYPE.HORIZONTAL_MULTI_PAGE_CONTAINER]: 'HMPC',
  [TEMPLATE_TYPE.SINGLE_PAGE_CONTAINER]: 'SPC',
}

export const RANKING_CRITERIA: Record<string, string> = {
  arpdl: 'Revenue / Downloads',
  cur_avg_ratings: 'Average rating',
  cur_total_ratings: 'Number of ratings',
  diff_dl_30: 'Downloads (d-30)',
  diff_dl_7: 'Downloads (d-7)',
  diff_dl_90: 'Downloads (d-90)',
  diff_revenue_30: 'Revenue (d-30)',
  diff_revenue_7: 'Revenue (d-7)',
  diff_revenue_90: 'Revenue (d-90)',
  download: 'Downloads',
  revenue: 'Revenue',
}

export const TEAM_NAME_MAPPING: Record<ChecklistTeam, string> = {
  admin: 'Admin',
  backend: 'Backend',
  business_development: 'Business Development',
  cesar: 'Cesar',
  cloud: 'Cloud',
  damien: 'Damien',
  data_team: 'Data',
  developers_team: 'Developers',
  developers_team_and_backend: 'Developers & Backend',
  ludo: 'Ludo',
  marketing_team: 'Marketing',
  monet: 'Monetization',
  monetization_team: 'Monetization',
  pm_and_mobile: 'PM & Mobile',
  pm_and_publishing: 'PM & Publishing',
  product_managers_team: 'Product managers',
  product_managers_team_and_business_development: 'PM & Business Development',
  product_managers_team_and_cesar: 'PM & Cesar',
  product_managers_team_and_rania: 'PM & Rania',
  product_monetization_squad_team: 'Product monetization squad',
  product_owners: 'Product owners',
  publishing: 'Publishing',
  rania: 'Rania',
  ua: 'UA',
  ua_and_mobile: 'UA & Mobile',
  ua_and_product_managers_team: 'UA & PM',
  ua_and_rania: 'UA & Rania',
}

export const VERSION_STATUS_LABEL_MAPPING: Record<VERSION_STATUS, string> = {
  [VERSION_STATUS.ACTIVE]: 'Active',
  [VERSION_STATUS.ARCHIVED]: 'Archived',
  [VERSION_STATUS.ERROR]: 'In error',
  [VERSION_STATUS.INACTIVE]: 'Inactive',
  [VERSION_STATUS.PREPARE]: 'Preparing',
  [VERSION_STATUS.PRODUCTION]: 'Production',
  [VERSION_STATUS.REMOVED]: 'Removed',
  [VERSION_STATUS.TESTING]: 'Testing',
  [VERSION_STATUS.UNKNOWN]: 'Unknown',
}

export const CHECKLIST_TEMPLATE_LABEL_MAPPING: Record<CHECKLIST_TEMPLATE, string> = {
  [CHECKLIST_TEMPLATE.NEW_MWM_APP]: 'New application',
  [CHECKLIST_TEMPLATE.NEW_PUBLISHING_APP]: 'New publishing application',
  [CHECKLIST_TEMPLATE.VERSION_RELEASE]: 'Version release',
}

export const ML_LABEL_MAPPING: Record<ML_LABEL, string> = {
  [ML_LABEL.BUG]: 'Bug',
  [ML_LABEL.BUSINESS_MODEL]: 'Business model',
  [ML_LABEL.FEATURE_SUGGESTION]: 'Feature suggestion',
  [ML_LABEL.NONE]: 'None',
  [ML_LABEL.RATING]: 'Rating',
  [ML_LABEL.TRASH]: 'Trash',
  [ML_LABEL.UX]: 'UX',
}

export const DEVICE_TYPE: Record<string, DeviceType> = {
  ANDROID: 'android',
  IOS: 'ios',
  WEB: 'web',
}

export const AUTHORIZATIONS = {
  ANALYTICS: {
    BUSINESS_REPORTS: {
      READ: 'analytics.business-reports.read',
    },
    FINANCIAL_REPORT: {
      READ: 'analytics.forecast.read',
    },
    GRAPH_BUILDER: {
      READ: 'analytics.graph-builder.read',
      WRITE: 'analytics.graph-builder.write',
    },
    LAYOUT_UPDATE: {
      WRITE: 'analytics.layouts.write',
    },
    UA_DASHBOARD: {
      READ: 'analytics.ua-dashboard.read',
    },
  },
  APP_SETUP: {
    TASK: {
      READ: 'app-setup.task.read',
      WRITE: 'app-setup.task.write',
    },
  },
  ASSETS_MANAGER: {
    ASSET: {
      READ: 'asset-manager.asset.read',
      WRITE: 'asset-manager.asset.write',
    },
  },
  AUTH: {
    AUTHORIZATIONS: {
      READ: 'auth.authorization.read',
      WRITE: 'auth.authorization.write',
    },
    GROUPS: {
      READ: 'auth.group.read',
      WRITE: 'auth.group.write',
    },
    ROLES: {
      READ: 'auth.role.read',
      WRITE: 'auth.role.write',
    },
    USERS: {
      READ: 'auth.user.read',
      WRITE: 'auth.user.write',
    },
  },
  CCC: {
    CATALOG: {
      READ: 'ccc.catalog.read',
      WRITE: 'ccc.catalog.write',
    },
    REPORTS: {
      READ: 'ccc.report.read',
      WRITE: 'ccc.report.write',
    },
  },
  DYNAMIC_SCREENS: {
    NAVIGATION_GRAPH: {
      READ: 'dynamic-screen.navigation-graph.read',
      WRITE: 'dynamic-screen.navigation-graph.write',
    },
    PAGE_CONTAINER: {
      READ: 'dynamic-screen.page-container.read',
      WRITE: 'dynamic-screen.page-container.write',
    },
    PLACEMENT: {
      READ: 'dynamic-screen.placement.read',
      WRITE: 'dynamic-screen.placement.write',
    },
    TEMPLATE: {
      READ: 'dynamic-screen.template.read',
    },
  },
  IAP: {
    FEATURES: {
      READ: 'iap.in-apps.read',
      WRITE: 'iap.in-apps.write',
    },
    FEATURES_GROUP: {
      READ: 'iap.in-apps.read',
      WRITE: 'iap.in-apps.write',
    },
    IN_APPS: {
      READ: 'iap.in-apps.read',
      WRITE: 'iap.in-apps.write',
    },
    OFFERS: {
      READ: 'iap.in-apps.read',
      WRITE: 'iap.in-apps.write',
    },
    V2: {
      READ: 'mwm-iap.offers.read',
    },
  },
  IN_TESTING_FEATURE: {
    READ: 'intranet.alpha-feature.read',
    // AUTH not existing yet, to be created if needed
    WRITE: 'intranet.alpha-feature.write',
  },
  MWM_USERS: {
    ACCOUNT: {
      READ: 'mwm-users.account.read',
      WRITE: 'mwm-users.account.write',
    },
  },
  REVIEWS: {
    READ: 'reviews.admin.read',
    WRITE: 'reviews.admin.write',
  },
  UA_MANAGEMENT: {
    READ: 'ua-manager.recommendation.read',
  },
  VERSION: {
    APPLICATION: {
      READ: 'version.application.read',
      WRITE: 'version.application.write',
    },
    CLUSTER: {
      READ: 'version.clusters.read',
      WRITE: 'version.clusters.write',
    },
    EVENT: {
      READ: 'version.event.read',
      WRITE: 'version.event.write',
    },
    GLOSSARY: {
      READ: 'version.glossary.read',
      WRITE: 'version.glossary.write',
    },
    TODOLIST: {
      READ: 'version.todolist.read',
      WRITE: 'version.todolist.write',
    },
    VIDEO: {
      READ: 'version.video.read',
      WRITE: 'version.video.write',
    },
  },
}

export const FORM_ERRORS = {
  AT_LEAST_ONE: 'You have to select at least one option',
  INVALID_CHARS: (pattern: string): string => `Accepted characters: ${pattern}`,
  INVALID_EMAIL: 'This should be a valid email format',
  MIN_VALUE: (min: number): string => `This value should be greater than or equal to ${min}`,
  NUMBER: 'Please enter a numeric value',
  REQUIRED: 'This field is required',
}

export const TOAST_ERRORS = {
  NOT_AUTHORIZED: 'You are not authorized to perform this action; contact an administrator',
}

export const dataNotAvailableText = 'No data available'

export const DEFAULT_ITEMS_PER_PAGE_VALUE = 1000

export const alphabet = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
]

export const LANGUAGE_BY_LOCALE: { [key: string]: string } = {
  af: 'Afrikaans',
  af_NA: 'Afrikaans (Namibia)',
  af_ZA: 'Afrikaans (South Africa)',
  ak: 'Akan',
  ak_GH: 'Akan (Ghana)',
  am: 'Amharic',
  am_ET: 'Amharic (Ethiopia)',
  ar: 'Arabic',
  ar_AE: 'Arabic (United Arab Emirates)',
  ar_BH: 'Arabic (Bahrain)',
  ar_DZ: 'Arabic (Algeria)',
  ar_EG: 'Arabic (Egypt)',
  ar_IQ: 'Arabic (Iraq)',
  ar_JO: 'Arabic (Jordan)',
  ar_KW: 'Arabic (Kuwait)',
  ar_LB: 'Arabic (Lebanon)',
  ar_LY: 'Arabic (Libya)',
  ar_MA: 'Arabic (Morocco)',
  ar_OM: 'Arabic (Oman)',
  ar_QA: 'Arabic (Qatar)',
  ar_SA: 'Arabic (Saudi Arabia)',
  ar_SD: 'Arabic (Sudan)',
  ar_SY: 'Arabic (Syria)',
  ar_TN: 'Arabic (Tunisia)',
  ar_YE: 'Arabic (Yemen)',
  as: 'Assamese',
  as_IN: 'Assamese (India)',
  asa: 'Asu',
  asa_TZ: 'Asu (Tanzania)',
  az: 'Azerbaijani',
  az_Cyrl: 'Azerbaijani (Cyrillic)',
  az_Cyrl_AZ: 'Azerbaijani (Cyrillic, Azerbaijan)',
  az_Latn: 'Azerbaijani (Latin)',
  az_Latn_AZ: 'Azerbaijani (Latin, Azerbaijan)',
  be: 'Belarusian',
  be_BY: 'Belarusian (Belarus)',
  bem: 'Bemba',
  bem_ZM: 'Bemba (Zambia)',
  bez: 'Bena',
  bez_TZ: 'Bena (Tanzania)',
  bg: 'Bulgarian',
  bg_BG: 'Bulgarian (Bulgaria)',
  bm: 'Bambara',
  bm_ML: 'Bambara (Mali)',
  bn: 'Bengali',
  bn_BD: 'Bengali (Bangladesh)',
  bn_IN: 'Bengali (India)',
  bo: 'Tibetan',
  bo_CN: 'Tibetan (China)',
  bo_IN: 'Tibetan (India)',
  bs: 'Bosnian',
  bs_BA: 'Bosnian (Bosnia and Herzegovina)',
  ca: 'Catalan',
  ca_ES: 'Catalan (Spain)',
  cgg: 'Chiga',
  cgg_UG: 'Chiga (Uganda)',
  chr: 'Cherokee',
  chr_US: 'Cherokee (United States)',
  cs: 'Czech',
  cs_CZ: 'Czech (Czech Republic)',
  cy: 'Welsh',
  cy_GB: 'Welsh (United Kingdom)',
  da: 'Danish',
  da_DK: 'Danish (Denmark)',
  dav: 'Taita',
  dav_KE: 'Taita (Kenya)',
  de: 'German',
  de_AT: 'German (Austria)',
  de_BE: 'German (Belgium)',
  de_CH: 'German (Switzerland)',
  de_DE: 'German (Germany)',
  de_LI: 'German (Liechtenstein)',
  de_LU: 'German (Luxembourg)',
  ebu: 'Embu',
  ebu_KE: 'Embu (Kenya)',
  ee: 'Ewe',
  ee_GH: 'Ewe (Ghana)',
  ee_TG: 'Ewe (Togo)',
  el: 'Greek',
  el_CY: 'Greek (Cyprus)',
  el_GR: 'Greek (Greece)',
  en: 'English',
  en_AS: 'English (American Samoa)',
  en_AU: 'English (Australia)',
  en_BE: 'English (Belgium)',
  en_BW: 'English (Botswana)',
  en_BZ: 'English (Belize)',
  en_CA: 'English (Canada)',
  en_GB: 'English (United Kingdom)',
  en_GU: 'English (Guam)',
  en_HK: 'English (Hong Kong SAR China)',
  en_IE: 'English (Ireland)',
  en_IL: 'English (Israel)',
  en_IN: 'English (India)',
  en_JM: 'English (Jamaica)',
  en_MH: 'English (Marshall Islands)',
  en_MP: 'English (Northern Mariana Islands)',
  en_MT: 'English (Malta)',
  en_MU: 'English (Mauritius)',
  en_NA: 'English (Namibia)',
  en_NZ: 'English (New Zealand)',
  en_PH: 'English (Philippines)',
  en_PK: 'English (Pakistan)',
  en_SG: 'English (Singapore)',
  en_TT: 'English (Trinidad and Tobago)',
  en_UM: 'English (U.S. Minor Outlying Islands)',
  en_US: 'English (United States)',
  en_VI: 'English (U.S. Virgin Islands)',
  en_ZA: 'English (South Africa)',
  en_ZW: 'English (Zimbabwe)',
  eo: 'Esperanto',
  es: 'Spanish',
  es_419: 'Spanish (Latin America)',
  es_AR: 'Spanish (Argentina)',
  es_BO: 'Spanish (Bolivia)',
  es_CL: 'Spanish (Chile)',
  es_CO: 'Spanish (Colombia)',
  es_CR: 'Spanish (Costa Rica)',
  es_DO: 'Spanish (Dominican Republic)',
  es_EC: 'Spanish (Ecuador)',
  es_ES: 'Spanish (Spain)',
  es_GQ: 'Spanish (Equatorial Guinea)',
  es_GT: 'Spanish (Guatemala)',
  es_HN: 'Spanish (Honduras)',
  es_MX: 'Spanish (Mexico)',
  es_NI: 'Spanish (Nicaragua)',
  es_PA: 'Spanish (Panama)',
  es_PE: 'Spanish (Peru)',
  es_PR: 'Spanish (Puerto Rico)',
  es_PY: 'Spanish (Paraguay)',
  es_SV: 'Spanish (El Salvador)',
  es_US: 'Spanish (United States)',
  es_UY: 'Spanish (Uruguay)',
  es_VE: 'Spanish (Venezuela)',
  et: 'Estonian',
  et_EE: 'Estonian (Estonia)',
  eu: 'Basque',
  eu_ES: 'Basque (Spain)',
  fa: 'Persian',
  fa_AF: 'Persian (Afghanistan)',
  fa_IR: 'Persian (Iran)',
  ff: 'Fulah',
  ff_SN: 'Fulah (Senegal)',
  fi: 'Finnish',
  fi_FI: 'Finnish (Finland)',
  fil: 'Filipino',
  fil_PH: 'Filipino (Philippines)',
  fo: 'Faroese',
  fo_FO: 'Faroese (Faroe Islands)',
  fr: 'French',
  fr_BE: 'French (Belgium)',
  fr_BF: 'French (Burkina Faso)',
  fr_BI: 'French (Burundi)',
  fr_BJ: 'French (Benin)',
  fr_BL: 'French (Saint Barthélemy)',
  fr_CA: 'French (Canada)',
  fr_CD: 'French (Congo - Kinshasa)',
  fr_CF: 'French (Central African Republic)',
  fr_CG: 'French (Congo - Brazzaville)',
  fr_CH: 'French (Switzerland)',
  fr_CI: 'French (Côte d’Ivoire)',
  fr_CM: 'French (Cameroon)',
  fr_DJ: 'French (Djibouti)',
  fr_FR: 'French (France)',
  fr_GA: 'French (Gabon)',
  fr_GN: 'French (Guinea)',
  fr_GP: 'French (Guadeloupe)',
  fr_GQ: 'French (Equatorial Guinea)',
  fr_KM: 'French (Comoros)',
  fr_LU: 'French (Luxembourg)',
  fr_MC: 'French (Monaco)',
  fr_MF: 'French (Saint Martin)',
  fr_MG: 'French (Madagascar)',
  fr_ML: 'French (Mali)',
  fr_MQ: 'French (Martinique)',
  fr_NE: 'French (Niger)',
  fr_RE: 'French (Réunion)',
  fr_RW: 'French (Rwanda)',
  fr_SN: 'French (Senegal)',
  fr_TD: 'French (Chad)',
  fr_TG: 'French (Togo)',
  ga: 'Irish',
  ga_IE: 'Irish (Ireland)',
  gl: 'Galician',
  gl_ES: 'Galician (Spain)',
  gsw: 'Swiss German',
  gsw_CH: 'Swiss German (Switzerland)',
  gu: 'Gujarati',
  gu_IN: 'Gujarati (India)',
  guz: 'Gusii',
  guz_KE: 'Gusii (Kenya)',
  gv: 'Manx',
  gv_GB: 'Manx (United Kingdom)',
  ha: 'Hausa',
  ha_Latn: 'Hausa (Latin)',
  ha_Latn_GH: 'Hausa (Latin, Ghana)',
  ha_Latn_NE: 'Hausa (Latin, Niger)',
  ha_Latn_NG: 'Hausa (Latin, Nigeria)',
  haw: 'Hawaiian',
  haw_US: 'Hawaiian (United States)',
  he: 'Hebrew',
  he_IL: 'Hebrew (Israel)',
  hi: 'Hindi',
  hi_IN: 'Hindi (India)',
  hr: 'Croatian',
  hr_HR: 'Croatian (Croatia)',
  hu: 'Hungarian',
  hu_HU: 'Hungarian (Hungary)',
  hy: 'Armenian',
  hy_AM: 'Armenian (Armenia)',
  id: 'Indonesian',
  id_ID: 'Indonesian (Indonesia)',
  ig: 'Igbo',
  ig_NG: 'Igbo (Nigeria)',
  ii: 'Sichuan Yi',
  ii_CN: 'Sichuan Yi (China)',
  is: 'Icelandic',
  is_IS: 'Icelandic (Iceland)',
  it: 'Italian',
  it_CH: 'Italian (Switzerland)',
  it_IT: 'Italian (Italy)',
  iw: 'Hebrew',
  ja: 'Japanese',
  ja_JP: 'Japanese (Japan)',
  jmc: 'Machame',
  jmc_TZ: 'Machame (Tanzania)',
  ka: 'Georgian',
  ka_GE: 'Georgian (Georgia)',
  kab: 'Kabyle',
  kab_DZ: 'Kabyle (Algeria)',
  kam: 'Kamba',
  kam_KE: 'Kamba (Kenya)',
  kde: 'Makonde',
  kde_TZ: 'Makonde (Tanzania)',
  kea: 'Kabuverdianu',
  kea_CV: 'Kabuverdianu (Cape Verde)',
  khq: 'Koyra Chiini',
  khq_ML: 'Koyra Chiini (Mali)',
  ki: 'Kikuyu',
  ki_KE: 'Kikuyu (Kenya)',
  kk: 'Kazakh',
  kk_Cyrl: 'Kazakh (Cyrillic)',
  kk_Cyrl_KZ: 'Kazakh (Cyrillic, Kazakhstan)',
  kl: 'Kalaallisut',
  kl_GL: 'Kalaallisut (Greenland)',
  kln: 'Kalenjin',
  kln_KE: 'Kalenjin (Kenya)',
  km: 'Khmer',
  km_KH: 'Khmer (Cambodia)',
  kn: 'Kannada',
  kn_IN: 'Kannada (India)',
  ko: 'Korean',
  ko_KR: 'Korean (South Korea)',
  kok: 'Konkani',
  kok_IN: 'Konkani (India)',
  kw: 'Cornish',
  kw_GB: 'Cornish (United Kingdom)',
  lag: 'Langi',
  lag_TZ: 'Langi (Tanzania)',
  lg: 'Ganda',
  lg_UG: 'Ganda (Uganda)',
  lt: 'Lithuanian',
  lt_LT: 'Lithuanian (Lithuania)',
  luo: 'Luo',
  luo_KE: 'Luo (Kenya)',
  luy: 'Luyia',
  luy_KE: 'Luyia (Kenya)',
  lv: 'Latvian',
  lv_LV: 'Latvian (Latvia)',
  mas: 'Masai',
  mas_KE: 'Masai (Kenya)',
  mas_TZ: 'Masai (Tanzania)',
  mer: 'Meru',
  mer_KE: 'Meru (Kenya)',
  mfe: 'Morisyen',
  mfe_MU: 'Morisyen (Mauritius)',
  mg: 'Malagasy',
  mg_MG: 'Malagasy (Madagascar)',
  mk: 'Macedonian',
  mk_MK: 'Macedonian (Macedonia)',
  ml: 'Malayalam',
  ml_IN: 'Malayalam (India)',
  mr: 'Marathi',
  mr_IN: 'Marathi (India)',
  ms: 'Malay',
  ms_BN: 'Malay (Brunei)',
  ms_MY: 'Malay (Malaysia)',
  mt: 'Maltese',
  mt_MT: 'Maltese (Malta)',
  my: 'Burmese',
  my_MM: 'Burmese (Myanmar [Burma])',
  naq: 'Nama',
  naq_NA: 'Nama (Namibia)',
  nb: 'Norwegian Bokmål',
  nb_NO: 'Norwegian Bokmål (Norway)',
  nd: 'North Ndebele',
  nd_ZW: 'North Ndebele (Zimbabwe)',
  ne: 'Nepali',
  ne_IN: 'Nepali (India)',
  ne_NP: 'Nepali (Nepal)',
  nl: 'Dutch',
  nl_BE: 'Dutch (Belgium)',
  nl_NL: 'Dutch (Netherlands)',
  nn: 'Norwegian Nynorsk',
  nn_NO: 'Norwegian Nynorsk (Norway)',
  nyn: 'Nyankole',
  nyn_UG: 'Nyankole (Uganda)',
  om: 'Oromo',
  om_ET: 'Oromo (Ethiopia)',
  om_KE: 'Oromo (Kenya)',
  or: 'Oriya',
  or_IN: 'Oriya (India)',
  pa: 'Punjabi',
  pa_Arab: 'Punjabi (Arabic)',
  pa_Arab_PK: 'Punjabi (Arabic, Pakistan)',
  pa_Guru: 'Punjabi (Gurmukhi)',
  pa_Guru_IN: 'Punjabi (Gurmukhi, India)',
  pl: 'Polish',
  pl_PL: 'Polish (Poland)',
  ps: 'Pashto',
  ps_AF: 'Pashto (Afghanistan)',
  pt: 'Portuguese',
  pt_BR: 'Portuguese (Brazil)',
  pt_GW: 'Portuguese (Guinea-Bissau)',
  pt_MZ: 'Portuguese (Mozambique)',
  pt_PT: 'Portuguese (Portugal)',
  rm: 'Romansh',
  rm_CH: 'Romansh (Switzerland)',
  ro: 'Romanian',
  ro_MD: 'Romanian (Moldova)',
  ro_RO: 'Romanian (Romania)',
  rof: 'Rombo',
  rof_TZ: 'Rombo (Tanzania)',
  ru: 'Russian',
  ru_MD: 'Russian (Moldova)',
  ru_RU: 'Russian (Russia)',
  ru_UA: 'Russian (Ukraine)',
  rw: 'Kinyarwanda',
  rw_RW: 'Kinyarwanda (Rwanda)',
  rwk: 'Rwa',
  rwk_TZ: 'Rwa (Tanzania)',
  saq: 'Samburu',
  saq_KE: 'Samburu (Kenya)',
  seh: 'Sena',
  seh_MZ: 'Sena (Mozambique)',
  ses: 'Koyraboro Senni',
  ses_ML: 'Koyraboro Senni (Mali)',
  sg: 'Sango',
  sg_CF: 'Sango (Central African Republic)',
  shi: 'Tachelhit',
  shi_Latn: 'Tachelhit (Latin)',
  shi_Latn_MA: 'Tachelhit (Latin, Morocco)',
  shi_Tfng: 'Tachelhit (Tifinagh)',
  shi_Tfng_MA: 'Tachelhit (Tifinagh, Morocco)',
  si: 'Sinhala',
  si_LK: 'Sinhala (Sri Lanka)',
  sk: 'Slovak',
  sk_SK: 'Slovak (Slovakia)',
  sl: 'Slovenian',
  sl_SI: 'Slovenian (Slovenia)',
  sn: 'Shona',
  sn_ZW: 'Shona (Zimbabwe)',
  so: 'Somali',
  so_DJ: 'Somali (Djibouti)',
  so_ET: 'Somali (Ethiopia)',
  so_KE: 'Somali (Kenya)',
  so_SO: 'Somali (Somalia)',
  sq: 'Albanian',
  sq_AL: 'Albanian (Albania)',
  sr: 'Serbian',
  sr_Cyrl: 'Serbian (Cyrillic)',
  sr_Cyrl_BA: 'Serbian (Cyrillic, Bosnia and Herzegovina)',
  sr_Cyrl_ME: 'Serbian (Cyrillic, Montenegro)',
  sr_Cyrl_RS: 'Serbian (Cyrillic, Serbia)',
  sr_Latn: 'Serbian (Latin)',
  sr_Latn_BA: 'Serbian (Latin, Bosnia and Herzegovina)',
  sr_Latn_ME: 'Serbian (Latin, Montenegro)',
  sr_Latn_RS: 'Serbian (Latin, Serbia)',
  sv: 'Swedish',
  sv_FI: 'Swedish (Finland)',
  sv_SE: 'Swedish (Sweden)',
  sw: 'Swahili',
  sw_KE: 'Swahili (Kenya)',
  sw_TZ: 'Swahili (Tanzania)',
  ta: 'Tamil',
  ta_IN: 'Tamil (India)',
  ta_LK: 'Tamil (Sri Lanka)',
  te: 'Telugu',
  te_IN: 'Telugu (India)',
  teo: 'Teso',
  teo_KE: 'Teso (Kenya)',
  teo_UG: 'Teso (Uganda)',
  th: 'Thai',
  th_TH: 'Thai (Thailand)',
  ti: 'Tigrinya',
  ti_ER: 'Tigrinya (Eritrea)',
  ti_ET: 'Tigrinya (Ethiopia)',
  to: 'Tonga',
  to_TO: 'Tonga (Tonga)',
  tr: 'Turkish',
  tr_TR: 'Turkish (Turkey)',
  tzm: 'Central Morocco Tamazight',
  tzm_Latn: 'Central Morocco Tamazight (Latin)',
  tzm_Latn_MA: 'Central Morocco Tamazight (Latin, Morocco)',
  uk: 'Ukrainian',
  uk_UA: 'Ukrainian (Ukraine)',
  ur: 'Urdu',
  ur_IN: 'Urdu (India)',
  ur_PK: 'Urdu (Pakistan)',
  uz: 'Uzbek',
  uz_Arab: 'Uzbek (Arabic)',
  uz_Arab_AF: 'Uzbek (Arabic, Afghanistan)',
  uz_Cyrl: 'Uzbek (Cyrillic)',
  uz_Cyrl_UZ: 'Uzbek (Cyrillic, Uzbekistan)',
  uz_Latn: 'Uzbek (Latin)',
  uz_Latn_UZ: 'Uzbek (Latin, Uzbekistan)',
  vi: 'Vietnamese',
  vi_VN: 'Vietnamese (Vietnam)',
  vun: 'Vunjo',
  vun_TZ: 'Vunjo (Tanzania)',
  xog: 'Soga',
  xog_UG: 'Soga (Uganda)',
  yo: 'Yoruba',
  yo_NG: 'Yoruba (Nigeria)',
  yue_Hant_HK: 'Cantonese (Traditional, Hong Kong SAR China)',
  zh: 'Chinese',
  ['zh-Hans']: 'Chinese (Simplified Han)',
  ['zh-Hant']: 'Chinese (Traditional Han)',
  zh_CN: 'Chinese - China (CN)',
  zh_HK: 'Chinese - Hong-Kong (HK)',
  zh_Hans: 'Chinese (Simplified Han)',
  zh_Hans_CN: 'Chinese (Simplified Han, China)',
  zh_Hans_HK: 'Chinese (Simplified Han, Hong Kong SAR China)',
  zh_Hans_MO: 'Chinese (Simplified Han, Macau SAR China)',
  zh_Hans_SG: 'Chinese (Simplified Han, Singapore)',
  zh_Hant: 'Chinese (Traditional Han)',
  zh_Hant_HK: 'Chinese (Traditional Han, Hong Kong SAR China)',
  zh_Hant_MO: 'Chinese (Traditional Han, Macau SAR China)',
  zh_Hant_TW: 'Chinese (Traditional Han, Taiwan)',
  zh_SG: 'Chinese - Singapour (SG)',
  zh_TW: 'Chinese - Taiwan (TW)',
  zu: 'Zulu',
  zu_ZA: 'Zulu (South Africa)',
}

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const SENTRY_COMMONS = {
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
  // NOTE: ignore list based off: https://gist.github.com/1878283
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    'globalThis is not defined',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    'Script error.',
    // Network errors such as going offline or being blocked by a proxy
    'Failed to fetch',
    'Network Error',
    'Load failed',
    // Stripe Error
    'Failed to load Stripe.js',
  ],
  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 1,
}
